<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-cash-register me-2"></i>{{ $t('page.finance_controls', 'Finance Controls') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='shifts' }"    @click="tab='shifts'; loadShifts()"><i class="mdi mdi-cash-register me-1"></i>Cashier Shifts</button>
        <button class="hms-tab" :class="{ active: tab==='breakdown' }" @click="tab='breakdown'; loadBreakdown()"><i class="mdi mdi-chart-donut me-1"></i>Collections Breakdown</button>
        <button class="hms-tab" :class="{ active: tab==='approvals' }" @click="tab='approvals'; loadApprovals()"><i class="mdi mdi-gavel me-1"></i>Approvals</button>
        <button class="hms-tab" :class="{ active: tab==='modes' }"     @click="tab='modes'; loadModes()"><i class="mdi mdi-credit-card-outline me-1"></i>Payment Modes</button>
        <button class="hms-tab" :class="{ active: tab==='aging' }"     @click="tab='aging'; loadAging()"><i class="mdi mdi-clock-alert-outline me-1"></i>Debtor Aging</button>
        <button class="hms-tab" :class="{ active: tab==='ifmis' }"     @click="tab='ifmis'; loadIfmis()"><i class="mdi mdi-bank me-1"></i>IFMIS / Banking</button>
      </div>

      <!-- ============ COLLECTIONS BREAKDOWN ============ -->
      <div v-if="tab==='breakdown'">
        <div class="d-flex justify-content-between mb-3 align-items-end">
          <h5 class="m-0">Collections by source</h5>
          <div class="d-flex gap-2 align-items-center">
            <select v-model="bdPreset" @change="onBdPreset" class="hms-input hms-input-sm">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="last_7">Last 7 days</option>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="custom">Custom…</option>
            </select>
            <template v-if="bdPreset === 'custom'">
              <input type="date" v-model="bdFrom" @change="loadBreakdown" class="hms-input hms-input-sm" />
              <span>→</span>
              <input type="date" v-model="bdTo"   @change="loadBreakdown" class="hms-input hms-input-sm" />
            </template>
            <button class="hms-btn hms-btn-blue" @click="loadBreakdown"><i class="mdi mdi-refresh"></i></button>
          </div>
        </div>

        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num">KSh {{ formatN(breakdown.grand_total || 0) }}</div><div class="hms-stat-lbl">Total collected</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ breakdown.grand_count || 0 }}</div><div class="hms-stat-lbl">Transactions</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-amber">KSh {{ formatN((breakdown.outstanding && breakdown.outstanding.total) || 0) }}</div><div class="hms-stat-lbl">Outstanding (window)</div></div>
        </div>

        <table class="hms-table">
          <thead><tr><th>Source</th><th class="text-end">Count</th><th class="text-end">Amount (KES)</th><th class="text-end">Share</th><th>Visualisation</th></tr></thead>
          <tbody>
            <tr v-for="r in (breakdown.rows || [])" :key="r.source">
              <td><i class="mdi" :class="bdIcon(r.source)" :style="{ color: bdColor(r.source) }"></i> <strong class="ms-2">{{ r.label }}</strong></td>
              <td class="text-end">{{ r.count }}</td>
              <td class="text-end"><strong>{{ formatN(r.amount) }}</strong></td>
              <td class="text-end">{{ r.share_pct }}%</td>
              <td>
                <div style="background:#e2e8f0; border-radius:6px; height:14px; width:100%; overflow:hidden;">
                  <div :style="{ width: r.share_pct + '%', background: bdColor(r.source), height:'100%' }"></div>
                </div>
              </td>
            </tr>
            <tr v-if="!(breakdown.rows && breakdown.rows.length)">
              <td colspan="5" class="text-center hms-muted">No collections in this window.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CASHIER SHIFTS ============ -->
      <div v-if="tab==='shifts'">
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ shiftStats.open_count || 0 }}</div><div class="hms-stat-lbl">Open shifts</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ shiftStats.closed_today || 0 }}</div><div class="hms-stat-lbl">Closed today</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ shiftStats.flagged_count || 0 }}</div><div class="hms-stat-lbl">Flagged variance</div></div>
          <div class="hms-stat"><div class="hms-stat-num">KSh {{ formatN(shiftStats.revenue_today || 0) }}</div><div class="hms-stat-lbl">Revenue today</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">KSh {{ formatN(shiftStats.flagged_variance || 0) }}</div><div class="hms-stat-lbl">Flagged variance KSh</div></div>
        </div>

        <div v-if="activeShift" class="hms-card mb-3 active-shift">
          <div class="hms-card-head"><i class="mdi mdi-cash-register me-1"></i>Your Active Shift #{{ activeShift.shift_id }}</div>
          <div class="hms-card-body">
            <div class="hms-grid-3">
              <div><span class="hms-vk">Opened</span> <strong>{{ formatTime(activeShift.opened_at) }}</strong></div>
              <div><span class="hms-vk">Float</span> <strong>KSh {{ formatN(activeShift.opening_float) }}</strong></div>
              <div><span class="hms-vk">Till</span> {{ activeShift.till_label || '—' }}</div>
              <div><span class="hms-vk">Collected</span> <strong>KSh {{ formatN(activeShift.collected_so_far || 0) }}</strong></div>
              <div><span class="hms-vk">Payments</span> {{ activeShift.payments_count_now || 0 }}</div>
              <div><button class="hms-btn hms-btn-success" @click="openCloseModal()"><i class="mdi mdi-flag-checkered"></i> Close Shift</button></div>
            </div>
          </div>
        </div>
        <div v-else class="hms-card mb-3">
          <div class="hms-card-body d-flex justify-content-between align-items-center">
            <div>You don't have an open shift. Open one to start collecting payments.</div>
            <button class="hms-btn hms-btn-success" @click="openShiftModal()"><i class="mdi mdi-plus"></i> Open Shift</button>
          </div>
        </div>

        <div class="hms-card">
          <div class="hms-card-head d-flex justify-content-between">
            <span>All shifts</span>
            <select v-model="shiftStatusFilter" class="hms-input" @change="loadShifts" style="max-width:180px">
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="flagged">Flagged</option>
              <option value="reconciled">Reconciled</option>
            </select>
          </div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>Shift</th><th>Cashier</th><th>Till</th><th>Opened</th><th>Closed</th><th>Float</th><th>Cash collected</th><th>Declared</th><th>Variance</th><th>Status</th><th></th></tr></thead>
              <tbody>
                <tr v-for="s in shifts" :key="s.shift_id" :class="s.status === 'flagged' ? 'row-action' : ''">
                  <td><strong>#{{ s.shift_id }}</strong></td>
                  <td>{{ s.cashier_name }}</td>
                  <td>{{ s.till_label || '—' }}</td>
                  <td>{{ formatTime(s.opened_at) }}</td>
                  <td>{{ s.closed_at ? formatTime(s.closed_at) : '—' }}</td>
                  <td>KSh {{ formatN(s.opening_float) }}</td>
                  <td>KSh {{ formatN(s.cash_collected) }}</td>
                  <td>{{ s.declared_cash != null ? 'KSh ' + formatN(s.declared_cash) : '—' }}</td>
                  <td :class="s.variance != null && Math.abs(s.variance) > 0.01 ? 'st-bad' : ''">
                    <strong v-if="s.variance != null">KSh {{ formatN(s.variance) }}</strong>
                    <span v-else class="hms-muted-sm">—</span>
                  </td>
                  <td><span class="hms-pill" :class="shiftPill(s.status)">{{ s.status }}</span></td>
                  <td>
                    <button v-if="s.status === 'flagged'" class="hms-btn hms-btn-success hms-btn-sm" @click="reviewShift(s, 'reconciled')">Reconcile</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ APPROVALS ============ -->
      <div v-if="tab==='approvals'">
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ approvalStats.pending || 0 }}</div><div class="hms-stat-lbl">Pending</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ approvalStats.approved || 0 }}</div><div class="hms-stat-lbl">Approved</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ approvalStats.rejected || 0 }}</div><div class="hms-stat-lbl">Rejected</div></div>
          <div class="hms-stat"><div class="hms-stat-num">KSh {{ formatN(approvalStats.amount_30d || 0) }}</div><div class="hms-stat-lbl">Amount approved (30d)</div></div>
          <div class="hms-stat"><button class="hms-btn hms-btn-success" @click="openApprovalReqModal()"><i class="mdi mdi-plus"></i> New Request</button></div>
        </div>
        <div class="d-flex gap-2 mb-2">
          <select v-model="approvalStatusFilter" class="hms-input" @change="loadApprovals" style="max-width:200px">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="applied">Applied</option>
            <option value="rejected">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
        <table class="hms-table">
          <thead><tr><th>#</th><th>Type</th><th>Patient / Invoice</th><th>Amount</th><th>Reason</th><th>Requested by</th><th>Status</th><th>Decision</th><th></th></tr></thead>
          <tbody>
            <tr v-for="a in approvals" :key="a.approval_id">
              <td>#{{ a.approval_id }}</td>
              <td><span class="hms-pill" :class="approvalTypePill(a.approval_type)">{{ a.approval_type }}</span></td>
              <td>{{ a.patient_name || '—' }} <span class="hms-muted-sm">{{ a.invoice_number ? '(' + a.invoice_number + ')' : '' }}</span></td>
              <td><strong>KSh {{ formatN(a.amount) }}</strong></td>
              <td>{{ a.reason }}</td>
              <td>{{ a.requested_by_name }}<div class="hms-muted-sm">{{ formatTime(a.requested_at) }}</div></td>
              <td><span class="hms-pill" :class="approvalStatusPill(a.status)">{{ a.status }}</span></td>
              <td><span v-if="a.decided_by_name">{{ a.decided_by_name }}<div class="hms-muted-sm">{{ a.decision_notes }}</div></span></td>
              <td>
                <template v-if="a.status === 'pending'">
                  <button class="hms-btn hms-btn-success hms-btn-sm" @click="decideApproval(a, 'approved')">Approve</button>
                  <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="decideApproval(a, 'rejected')">Reject</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ PAYMENT MODES ============ -->
      <div v-if="tab==='modes'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="modePeriod" class="hms-input" @change="loadModes" style="max-width:200px">
            <option value="last_30d">Last 30 days</option>
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-card mb-3">
          <div class="hms-card-head">Revenue by payment mode — {{ formatN(modeTotals.grand_total || 0) }} KSh total · {{ modeTotals.payment_count || 0 }} payments</div>
          <div class="hms-card-body">
            <div v-for="m in modesData" :key="m.payment_method" class="hms-mode-row">
              <div class="hms-mode-label">
                <i :class="modeIcon(m.payment_method)"></i>
                <strong>{{ formatMode(m.payment_method) }}</strong>
                <span class="hms-muted-sm">{{ m.payment_count }} payments · avg KSh {{ formatN(m.avg_amount) }}</span>
              </div>
              <div class="hms-mode-bar"><div class="hms-mode-fill" :style="{ width: modePct(m.total_amount) + '%', background: modeColor(m.payment_method) }"></div></div>
              <div class="hms-mode-amt"><strong>KSh {{ formatN(m.total_amount) }}</strong><div class="hms-muted-sm">{{ modePct(m.total_amount) }}%</div></div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ DEBTOR AGING ============ -->
      <div v-if="tab==='aging'">
        <div class="hms-stat-row mb-3">
          <div class="hms-stat"><div class="hms-stat-num">KSh {{ formatN(grandTotal) }}</div><div class="hms-stat-lbl">Total outstanding</div></div>
          <div class="hms-stat" v-for="b in agingBuckets" :key="b.code" :class="b.cls">
            <div class="hms-stat-num">KSh {{ formatN(bucketTotals[b.code] || 0) }}</div>
            <div class="hms-stat-lbl">{{ b.label }} <span class="hms-muted-sm">({{ bucketCounts[b.code] || 0 }})</span></div>
          </div>
        </div>
        <table class="hms-table">
          <thead><tr><th>Invoice</th><th>Patient</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Age</th><th>Bucket</th><th>Phone</th></tr></thead>
          <tbody>
            <tr v-for="d in debtors" :key="d.invoice_id" :class="bucketRowClass(d.bucket)">
              <td><strong>{{ d.invoice_number }}</strong><div class="hms-muted-sm">{{ d.created_at }}</div></td>
              <td>{{ d.patient_name }}<div class="hms-muted-sm">{{ d.patient_no }}</div></td>
              <td>KSh {{ formatN(d.total_amount) }}</td>
              <td>KSh {{ formatN(d.paid_amount) }}</td>
              <td><strong class="st-bad">KSh {{ formatN(d.outstanding) }}</strong></td>
              <td>{{ d.days_old }}d</td>
              <td><span class="hms-pill" :class="bucketPill(d.bucket)">{{ d.bucket }}</span></td>
              <td>{{ d.phone || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ IFMIS / BANKING ============ -->
      <div v-if="tab==='ifmis'">
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">IFMIS — push monthly revenue</div>
            <div class="hms-card-body">
              <div class="row g-2">
                <div class="col-6"><label class="hms-label">Period (YYYY-MM)</label><input v-model="ifmis.period" class="hms-input" /></div>
                <div class="col-6"><label class="hms-label">Vote head</label><input v-model="ifmis.vote_head" class="hms-input" placeholder="e.g. 09-2410-0001" /></div>
                <div class="col-12 d-flex gap-2 mt-2">
                  <button class="hms-btn hms-btn-blue"    :disabled="busy" @click="doIfmisPush(true)"><i class="mdi mdi-test-tube"></i> Simulate</button>
                  <button class="hms-btn hms-btn-success" :disabled="busy" @click="doIfmisPush(false)"><i class="mdi mdi-cloud-upload"></i> Push to IFMIS</button>
                </div>
              </div>
              <div v-if="ifmis.lastResult" class="hms-result-banner mt-3" :class="'res-' + ifmis.lastResult.status">
                <strong>{{ ifmis.lastResult.status }}</strong> · KSh {{ formatN(ifmis.lastResult.amount) }} · ref {{ ifmis.lastResult.posting_ref }}
              </div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Bank reconciliation</div>
            <div class="hms-card-body">
              <button class="hms-btn hms-btn-blue" :disabled="busy" @click="doBankImport"><i class="mdi mdi-cloud-download"></i> Import statement (simulate)</button>
              <select v-model="bankStatusFilter" class="hms-input ms-2" @change="loadBankLines" style="display:inline-block; width:200px">
                <option value="">All lines</option>
                <option value="unmatched">Unmatched</option>
                <option value="matched">Matched</option>
              </select>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Recent IFMIS postings</div>
            <div class="hms-card-body p-0">
              <table v-if="ifmisPostings.length" class="hms-table">
                <thead><tr><th>Posted</th><th>Period</th><th>Amount</th><th>Status</th><th>Ref</th></tr></thead>
                <tbody>
                  <tr v-for="p in ifmisPostings" :key="p.posting_id">
                    <td>{{ formatTime(p.created_at) }}</td>
                    <td>{{ p.revenue_period }}</td>
                    <td>KSh {{ formatN(p.amount) }}</td>
                    <td><span class="hms-pill" :class="postingPill(p.status)">{{ p.status }}</span></td>
                    <td class="hms-muted-sm">{{ p.posting_ref }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No IFMIS postings yet.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Bank statement lines</div>
            <div class="hms-card-body p-0">
              <table v-if="bankLines.length" class="hms-table">
                <thead><tr><th>Date</th><th>Narration</th><th>Amount</th><th>Status</th></tr></thead>
                <tbody>
                  <tr v-for="b in bankLines" :key="b.line_id">
                    <td>{{ b.value_date }}</td>
                    <td>{{ b.narration }}<div class="hms-muted-sm">{{ b.bank_reference }}</div></td>
                    <td>KSh {{ formatN(b.amount) }}</td>
                    <td><span class="hms-pill" :class="b.match_status === 'matched' ? 'hms-pill-success' : 'hms-pill-warn'">{{ b.match_status }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-pad text-center hms-muted">No bank lines imported yet.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ====== OPEN SHIFT MODAL ====== -->
    <div v-if="openModal" class="hms-modal-bg" @click.self="openModal=false">
      <div class="hms-modal" style="width:480px">
        <div class="hms-modal-head"><h5>Open Cashier Shift</h5><button class="hms-icon-btn" @click="openModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Till / Counter label</label><input v-model="openForm.till_label" class="hms-input" placeholder="OPD-1, Pharmacy…" /></div>
            <div class="col-6"><label class="hms-label">Shift type</label>
              <select v-model="openForm.shift_type" class="hms-input">
                <option value="morning">Morning</option><option value="afternoon">Afternoon</option><option value="night">Night</option><option value="custom">Custom</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Opening float (KSh)</label><input v-model.number="openForm.opening_float" type="number" min="0" step="100" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="openModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveOpenShift">{{ saving?'Opening…':'Open Shift' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== CLOSE SHIFT MODAL ====== -->
    <div v-if="closeModal" class="hms-modal-bg" @click.self="closeModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Close Cashier Shift #{{ activeShift && activeShift.shift_id }}</h5><button class="hms-icon-btn" @click="closeModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="hms-info-banner">
            <div>Opening float: <strong>KSh {{ formatN(activeShift && activeShift.opening_float) }}</strong></div>
            <div>Cash payments collected: <strong>KSh {{ formatN(activeShift && activeShift.collected_so_far) }}</strong></div>
            <div>Expected in till: <strong>KSh {{ formatN(expectedCash) }}</strong></div>
          </div>
          <div class="row g-2 mt-2">
            <div class="col-12"><label class="hms-label">Declared cash counted (KSh) *</label>
              <input v-model.number="closeForm.declared_cash" type="number" step="0.01" class="hms-input" />
              <div v-if="closeForm.declared_cash != null" class="mt-1" :class="varianceClass">
                Variance: <strong>KSh {{ formatN(closeForm.declared_cash - expectedCash) }}</strong>
              </div>
            </div>
            <div class="col-12"><label class="hms-label">Closing notes</label><textarea v-model="closeForm.closing_notes" class="hms-input" rows="2" placeholder="Anything unusual? Cash drops, robberies, etc."></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="closeModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveCloseShift">{{ saving?'Closing…':'Close Shift' }}</button>
        </div>
      </div>
    </div>

    <!-- ====== APPROVAL REQUEST MODAL ====== -->
    <div v-if="apprModal" class="hms-modal-bg" @click.self="apprModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Request approval</h5><button class="hms-icon-btn" @click="apprModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Type</label>
              <select v-model="apprForm.approval_type" class="hms-input">
                <option value="discount">Discount</option><option value="waiver">Waiver</option>
                <option value="refund">Refund</option><option value="cancellation">Cancel invoice</option>
                <option value="price_override">Price override</option><option value="write_off">Write-off</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Amount (KSh) *</label><input v-model.number="apprForm.amount" type="number" min="0" step="0.01" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Invoice ID</label><input v-model.number="apprForm.invoice_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Patient ID</label><input v-model.number="apprForm.patient_id" type="number" class="hms-input" /></div>
            <div class="col-12" v-if="apprForm.approval_type === 'refund'"><label class="hms-label">Payment ID *</label><input v-model.number="apprForm.payment_id" type="number" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Reason *</label><textarea v-model="apprForm.reason" class="hms-input" rows="3" placeholder="Why is this discount/waiver/refund needed?"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="apprModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveApprovalReq">{{ saving?'Submitting…':'Submit request' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "FinanceControls",
  components: { Navigation },
  data() {
    return {
      tab: 'shifts', saving: false, busy: false,

      // Collections breakdown
      bdPreset: 'today', bdFrom: '', bdTo: '',
      breakdown: { rows: [], grand_total: 0, grand_count: 0, outstanding: { total: 0, count: 0 } },

      shifts: [], shiftStats: {}, shiftStatusFilter: '',
      activeShift: null,

      approvals: [], approvalStats: {}, approvalStatusFilter: 'pending',

      modePeriod: 'last_30d',
      modesData: [], modeTotals: {},

      debtors: [], bucketTotals: {}, bucketCounts: {}, grandTotal: 0,
      agingBuckets: [
        { code: '0-30',  label: '0-30 days',  cls: '' },
        { code: '31-60', label: '31-60 days', cls: '' },
        { code: '61-90', label: '61-90 days', cls: '' },
        { code: '>90',   label: '>90 days',   cls: 'hms-stat-warn' }
      ],

      ifmis: { period: new Date().toISOString().substring(0,7), vote_head: '', lastResult: null },
      ifmisPostings: [],
      bankStatusFilter: '', bankLines: [],

      openModal: false, openForm: { till_label: '', shift_type: 'morning', opening_float: 0 },
      closeModal: false, closeForm: { declared_cash: null, closing_notes: '' },
      apprModal: false, apprForm: { approval_type: 'discount', amount: 0, invoice_id: null, payment_id: null, patient_id: null, reason: '' }
    };
  },
  computed: {
    expectedCash() {
      if (!this.activeShift) return 0;
      return parseFloat(this.activeShift.opening_float || 0) + parseFloat(this.activeShift.collected_so_far || 0);
    },
    varianceClass() {
      if (this.closeForm.declared_cash == null) return '';
      const v = this.closeForm.declared_cash - this.expectedCash;
      return Math.abs(v) < 0.01 ? 'st-good' : 'st-bad';
    }
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() { await Promise.all([this.loadShifts(), this.loadActiveShift()]); },

    // ===== COLLECTIONS BREAKDOWN =====
    onBdPreset() {
      var t = new Date(); var iso = function(d){ return d.toISOString().slice(0,10); };
      var addDays = function(d, n){ var x = new Date(d); x.setDate(x.getDate()+n); return x; };
      if (this.bdPreset === 'today')      { this.bdFrom = iso(t); this.bdTo = iso(t); }
      else if (this.bdPreset === 'yesterday')  { this.bdFrom = iso(addDays(t,-1)); this.bdTo = iso(addDays(t,-1)); }
      else if (this.bdPreset === 'last_7')     { this.bdFrom = iso(addDays(t,-6)); this.bdTo = iso(t); }
      else if (this.bdPreset === 'this_month') { this.bdFrom = iso(new Date(t.getFullYear(), t.getMonth(), 1)); this.bdTo = iso(t); }
      else if (this.bdPreset === 'last_month') {
        var first = new Date(t.getFullYear(), t.getMonth()-1, 1);
        var last  = new Date(t.getFullYear(), t.getMonth(), 0);
        this.bdFrom = iso(first); this.bdTo = iso(last);
      }
      if (this.bdPreset !== 'custom') this.loadBreakdown();
    },
    async loadBreakdown() {
      if (!this.bdFrom) this.onBdPreset();
      try {
        var r = await executeJson({
          function: 'getCollectionsBreakdown',
          from_iso: this.bdFrom + ' 00:00:00',
          to_iso:   this.bdTo   + ' 23:59:59'
        }, hms);
        if (r.data && r.data.success) this.breakdown = r.data.data || this.breakdown;
      } catch(e) { /* offline ok */ }
    },
    bdIcon(src) {
      return ({
        cash: 'mdi-cash', mpesa: 'mdi-cellphone', card: 'mdi-credit-card-outline',
        bank_transfer: 'mdi-bank', insurance: 'mdi-shield-check-outline', corporate: 'mdi-domain',
        waiver: 'mdi-tag-off-outline', sha: 'mdi-shield-key-outline', ntutu: 'mdi-shield-heart',
        insurance_claim: 'mdi-file-document-check-outline'
      })[src] || 'mdi-cash-multiple';
    },
    bdColor(src) {
      return ({
        cash: '#10B981', mpesa: '#059669', card: '#0EA5E9', bank_transfer: '#1E40AF',
        insurance: '#7C3AED', corporate: '#475569', waiver: '#F59E0B', sha: '#0284C7',
        ntutu: '#047857', insurance_claim: '#8B5CF6'
      })[src] || '#0047AB';
    },

    // ===== SHIFTS =====
    async loadShifts() {
      try {
        const r = await executeJson({ function: 'getCashierShifts', status: this.shiftStatusFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.shifts = d.Shifts || [];
          this.shiftStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadActiveShift() {
      try {
        const r = await executeJson({ function: 'getActiveCashierShift' }, hms);
        if (r.data && r.data.success) this.activeShift = (r.data.data && r.data.data.Shift) || null;
      } catch (e) { console.error(e); }
    },
    openShiftModal() { this.openForm = { till_label: '', shift_type: 'morning', opening_float: 0 }; this.openModal = true; },
    async saveOpenShift() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'openCashierShift', ...this.openForm }, hms);
        if (r.data && r.data.success) { this.openModal = false; await this.loadAll(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    openCloseModal() { this.closeForm = { declared_cash: null, closing_notes: '' }; this.closeModal = true; },
    async saveCloseShift() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'closeCashierShift', shift_id: this.activeShift.shift_id, ...this.closeForm }, hms);
        if (r.data && r.data.success) { this.closeModal = false; await this.loadAll(); alert(r.data.message); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async reviewShift(s, status) {
      const notes = prompt('Reviewer notes (will be appended to shift)?', '');
      if (notes === null) return;
      const r = await executeJson({ function: 'reviewCashierShift', shift_id: s.shift_id, status, notes }, hms);
      if (r.data && r.data.success) await this.loadShifts();
    },

    // ===== APPROVALS =====
    async loadApprovals() {
      try {
        const r = await executeJson({ function: 'getBillingApprovals', status: this.approvalStatusFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.approvals = d.Approvals || [];
          this.approvalStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    openApprovalReqModal() { this.apprForm = { approval_type: 'discount', amount: 0, invoice_id: null, payment_id: null, patient_id: null, reason: '' }; this.apprModal = true; },
    async saveApprovalReq() {
      if (!this.apprForm.reason || this.apprForm.amount <= 0) { alert('Reason and amount required.'); return; }
      this.saving = true;
      try {
        const r = await executeJson({ function: 'requestBillingApproval', ...this.apprForm }, hms);
        if (r.data && r.data.success) { this.apprModal = false; await this.loadApprovals(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },
    async decideApproval(a, decision) {
      const notes = prompt('Decision notes?', '');
      if (notes === null) return;
      const r = await executeJson({ function: 'decideBillingApproval', approval_id: a.approval_id, decision, notes }, hms);
      if (r.data && r.data.success) { await this.loadApprovals(); alert(r.data.message); }
      else alert((r.data && r.data.message) || 'Failed.');
    },

    // ===== PAYMENT MODES =====
    async loadModes() {
      try {
        const r = await executeJson({ function: 'getPaymentModeReport', period: this.modePeriod }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.modesData = d.ByMode || [];
          this.modeTotals = d.Totals || {};
        }
      } catch (e) { console.error(e); }
    },
    modePct(amount) {
      const t = parseFloat(this.modeTotals.grand_total || 0);
      return t > 0 ? Math.round((parseFloat(amount) / t) * 100) : 0;
    },
    modeIcon(m) { return ({ cash: 'mdi mdi-cash', mpesa: 'mdi mdi-cellphone', card: 'mdi mdi-credit-card', bank_transfer: 'mdi mdi-bank-transfer', insurance: 'mdi mdi-shield-check', corporate: 'mdi mdi-domain', waiver: 'mdi mdi-gift' })[m] || 'mdi mdi-cash'; },
    modeColor(m) { return ({ cash: '#16a34a', mpesa: '#16a34a', card: '#2b6cb0', bank_transfer: '#0891b2', insurance: '#7c3aed', corporate: '#d69e2e', waiver: '#94a3b8' })[m] || '#4299e1'; },
    formatMode(m) { return ({ cash: 'Cash', mpesa: 'M-Pesa', card: 'Card', bank_transfer: 'Bank transfer', insurance: 'Insurance / SHA', corporate: 'Corporate', waiver: 'Waiver' })[m] || m; },

    // ===== DEBTOR AGING =====
    async loadAging() {
      try {
        const r = await executeJson({ function: 'getDebtorAging' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.debtors = d.Debtors || [];
          this.bucketTotals = d.BucketTotals || {};
          this.bucketCounts = d.BucketCounts || {};
          this.grandTotal = d.GrandTotal || 0;
        }
      } catch (e) { console.error(e); }
    },
    bucketPill(b) { return ({ '0-30': 'hms-pill-success', '31-60': 'hms-pill-info', '61-90': 'hms-pill-warn', '>90': 'hms-pill-danger' })[b] || ''; },
    bucketRowClass(b) { return ({ '>90': 'row-action', '61-90': 'row-warn' })[b] || ''; },

    // ===== IFMIS / BANKING =====
    async loadIfmis() {
      try {
        const [p, b] = await Promise.all([
          executeJson({ function: 'getIfmisPostings' }, hms),
          executeJson({ function: 'getBankStatementLines', match_status: this.bankStatusFilter }, hms)
        ]);
        if (p.data && p.data.success) this.ifmisPostings = (p.data.data && p.data.data.Postings) || [];
        if (b.data && b.data.success) this.bankLines = (b.data.data && b.data.data.Lines) || [];
      } catch (e) { console.error(e); }
    },
    async loadBankLines() {
      try {
        const r = await executeJson({ function: 'getBankStatementLines', match_status: this.bankStatusFilter }, hms);
        if (r.data && r.data.success) this.bankLines = (r.data.data && r.data.data.Lines) || [];
      } catch (e) { console.error(e); }
    },
    async doIfmisPush(simulate) {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'pushToIfmis', period: this.ifmis.period, vote_head: this.ifmis.vote_head, simulate: simulate ? 1 : 0 }, hms);
        if (r.data && r.data.success) { this.ifmis.lastResult = r.data.data; await this.loadIfmis(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },
    async doBankImport() {
      this.busy = true;
      try {
        const r = await executeJson({ function: 'importBankStatement', simulate: 1 }, hms);
        if (r.data && r.data.success) { await this.loadIfmis(); alert(r.data.message); }
      } catch (e) { console.error(e); }
      finally { this.busy = false; }
    },

    // ===== formatting =====
    shiftPill(s) { return ({ open: 'hms-pill-info', closed: 'hms-pill-success', flagged: 'hms-pill-danger', reconciled: 'hms-pill-success' })[s] || ''; },
    approvalTypePill(t) { return ({ discount: 'hms-pill-info', waiver: 'hms-pill-warn', refund: 'hms-pill-warn', cancellation: 'hms-pill-danger', price_override: 'hms-pill-info', write_off: 'hms-pill-danger' })[t] || ''; },
    approvalStatusPill(s) { return ({ pending: 'hms-pill-warn', approved: 'hms-pill-success', applied: 'hms-pill-success', rejected: 'hms-pill-danger', withdrawn: 'hms-pill-muted' })[s] || ''; },
    postingPill(s) { return ({ accepted: 'hms-pill-success', simulated: 'hms-pill-info', sent: 'hms-pill-info', pending: 'hms-pill-warn', rejected: 'hms-pill-danger' })[s] || ''; },
    formatN(n) { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); },
    formatTime(s) { if (!s) return '—'; const d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleString(); }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; flex-wrap: wrap; }
.hms-tab { padding: 10px 14px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 13px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 22px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; }
.hms-stat-warning, .hms-stat-warn { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card.active-shift { border-left: 4px solid #38a169; background: #f0fff4; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; display: flex; align-items: center; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.hms-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; align-items: center; }
.hms-vk { display: inline-block; min-width: 80px; color: #a0aec0; font-size: 10px; text-transform: uppercase; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-mode-row { display: grid; grid-template-columns: 280px 1fr 200px; gap: 16px; align-items: center; padding: 8px 0; border-bottom: 1px solid #edf2f7; }
.hms-mode-label .mdi { color: #4a5568; margin-right: 6px; font-size: 18px; vertical-align: middle; }
.hms-mode-bar  { background: #edf2f7; border-radius: 4px; height: 16px; overflow: hidden; }
.hms-mode-fill { height: 100%; transition: width .3s; }
.hms-mode-amt  { text-align: right; }

.hms-result-banner { padding: 8px 12px; border-radius: 6px; }
.hms-result-banner.res-accepted, .hms-result-banner.res-success { background: #f0fff4; border: 1px solid #9ae6b4; color: #22543d; }
.hms-result-banner.res-simulated { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; }
.hms-result-banner.res-rejected, .hms-result-banner.res-error { background: #fff5f5; border: 1px solid #fc8181; color: #742a2a; }

.hms-info-banner { background: #ebf8ff; border: 1px solid #90cdf4; color: #2c5282; padding: 10px 12px; border-radius: 6px; font-size: 13px; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.st-bad { color: #c53030; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
.ms-2 { margin-left: 8px; }
</style>
